import { useEffect, useState } from "react";

export const THEME_STORAGE_KEY = "techladder-theme";

export type ThemeName = "light" | "dark";

export function readStoredTheme(): ThemeName {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    // Private mode / blocked storage: fall back to light rather than throwing.
    return "light";
  }
}

export function applyTheme(theme: ThemeName): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/**
 * Stamp the persisted theme onto <html> before React renders.
 *
 * AppLayout also does this, but the fullscreen editors (e.g.
 * /home/create-automation) render outside AppLayout — without this a direct
 * load or refresh of those routes left `data-theme` unset and the page came up
 * light no matter what the user had chosen.
 */
export function bootstrapTheme(): void {
  applyTheme(readStoredTheme());
}

/** Palette the React Flow canvas needs as literal colours (SVG stroke/marker
 *  attributes cannot take Tailwind classes). */
export interface CanvasPalette {
  edge: string;
  edgeSelected: string;
  edgeError: string;
  /** Halo drawn under an edge so it stays readable over a node. */
  edgeHalo: string;
  dots: string;
}

const FALLBACK: CanvasPalette = {
  edge: "#A8A29E",
  edgeSelected: "#F97316",
  edgeError: "#DC2626",
  edgeHalo: "#FFFFFF",
  dots: "rgba(28,25,23,0.08)",
};

function readCanvasPalette(): CanvasPalette {
  if (typeof window === "undefined") return FALLBACK;
  const styles = getComputedStyle(document.documentElement);
  const token = (name: string, fallback: string) =>
    styles.getPropertyValue(name).trim() || fallback;
  return {
    edge: token("--color-ink-3", FALLBACK.edge),
    edgeSelected: token("--color-brand", FALLBACK.edgeSelected),
    edgeError: token("--color-danger", FALLBACK.edgeError),
    edgeHalo: token("--color-elevated", FALLBACK.edgeHalo),
    dots: token("--color-line", FALLBACK.dots),
  };
}

/**
 * Canvas colours resolved from the active theme's CSS variables, re-read when
 * the theme flips. Needed because React Flow edges/markers take literal colour
 * strings, which would otherwise be frozen at the light palette.
 */
export function useCanvasPalette(): CanvasPalette {
  const [palette, setPalette] = useState<CanvasPalette>(readCanvasPalette);

  useEffect(() => {
    const sync = () => setPalette(readCanvasPalette());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  return palette;
}
